import { Alert, Collapse, Descriptions, Empty, Space, Typography } from 'antd';

import { AUDIT_ROWS, CODE_ROWS, formatNumber, KIND_LABELS } from './shared';
import StepLog from './StepLog';

const { Text } = Typography;

/*
 * Résultat de la dernière opération, quel que soit son type. Placé dans son propre onglet
 * pour ne pas repousser les commandes en bas de page quand le rapport est long.
 */
const ResultPanel = ({ job }) => {
  if (!job || !job.report) return <Empty description="Aucun résultat à afficher pour le moment" />;

  const { report } = job;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type={job.dryRun ? 'info' : 'success'}
        showIcon
        message={`${KIND_LABELS[job.kind] || 'Opération'} — ${job.dryRun ? 'simulation : aucune donnée modifiée' : 'exécutée'}`}
        description={`Durée : ${Math.round(report.durationMs / 1000)} s${job.startedBy ? ` · lancée par ${job.startedBy}` : ''}`}
      />

      {/* Bascule d'année : un bloc par groupe de plateformes. */}
      {(report.groups || []).map(group => (
        <Descriptions
          key={group.platforms.join(',')}
          bordered
          column={2}
          size="small"
          title={`${group.platforms.join(', ')} — rentrée ${group.yearStart}`}>
          {AUDIT_ROWS.map(row => (
            <Descriptions.Item key={row.key} label={row.label}>
              {formatNumber(group.audit[row.key])}
            </Descriptions.Item>
          ))}
          <Descriptions.Item label="Enseignants à resynchroniser">{group.aResynchroniser}</Descriptions.Item>
          {group.audit.elevesSansClasse > 0 && (
            <Descriptions.Item label="Élèves sans classe (anomalie, hors périmètre)">
              {group.audit.elevesSansClasse}
            </Descriptions.Item>
          )}
        </Descriptions>
      ))}

      {/* Sélection de séries. */}
      {job.kind === 'code' && report.counts && (
        <Descriptions bordered column={2} size="small" title={`${report.oldCode || 'aucun retrait'} → ${report.newCode}`}>
          {CODE_ROWS.map(row => (
            <Descriptions.Item key={row.key} label={row.label}>
              {formatNumber(report.counts[row.key])}
            </Descriptions.Item>
          ))}
          <Descriptions.Item label="Plateformes visées" span={2}>
            {(report.plateformes || []).join(', ') || 'aucune'}
          </Descriptions.Item>
          {report.applied && (
            <Descriptions.Item label="Appliqué" span={2}>
              {formatNumber(report.applied.deblocagesRetires)} déblocages retirés ·{' '}
              {formatNumber(report.applied.rattachementsRetires)} rattachements retirés ·{' '}
              {formatNumber(report.applied.rattachementsAjoutes)} rattachements ajoutés ·{' '}
              {formatNumber(report.applied.deblocagesAjoutes)} déblocages ajoutés
            </Descriptions.Item>
          )}
        </Descriptions>
      )}

      {/* Rechargement des classes. */}
      {job.kind === 'classes' && (
        <Descriptions bordered column={2} size="small" title="Rechargement des classes">
          <Descriptions.Item label="Périmètre">
            {report.onlyStale ? 'enseignants en retard' : 'tous les enseignants'}
          </Descriptions.Item>
          <Descriptions.Item label="Plateformes">{(report.platforms || []).join(', ')}</Descriptions.Item>
          <Descriptions.Item label="Enseignants à recharger" span={2}>
            {formatNumber(report.aRecharger)}
            {report.parPlateforme && Object.keys(report.parPlateforme).length > 0
              ? ` (${Object.entries(report.parPlateforme)
                  .map(([projet, n]) => `${projet} : ${n}`)
                  .join(' · ')})`
              : ''}
          </Descriptions.Item>
        </Descriptions>
      )}

      {/* Resynchronisation Edifice : commune à la transition et au rechargement. */}
      {report.resync && (
        <Descriptions bordered column={2} size="small" title="Resynchronisation Edifice">
          <Descriptions.Item label="Enseignants resynchronisés">
            {report.resync.enseignants.ok} / {report.resync.enseignants.total}
          </Descriptions.Item>
          <Descriptions.Item label="Absents d’Edifice">{report.resync.enseignants.absentEdifice}</Descriptions.Item>
          <Descriptions.Item label="Classes rechargées">
            {report.resync.classes.ok} / {report.resync.classes.total}
          </Descriptions.Item>
          <Descriptions.Item label="Classes en erreur">{report.resync.classes.erreurs}</Descriptions.Item>
          <Descriptions.Item label="Élèves sortis supprimés">{report.resync.elevesSupprimes}</Descriptions.Item>
          <Descriptions.Item label="Parents supprimés">{report.resync.parentsSupprimes}</Descriptions.Item>
        </Descriptions>
      )}

      {report.resync && report.resync.totalErreurs > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${report.resync.totalErreurs} erreur(s) pendant la resynchronisation`}
          description={
            <ul style={{ marginBottom: 0 }}>
              {report.resync.erreurs.map(e => (
                <li key={e.idEdifice || e.id}>
                  {e.idEdifice || e.id} : {e.message || e.status}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {job.steps && job.steps.length > 0 && (
        <Collapse
          size="small"
          items={[
            {
              children: <StepLog steps={job.steps} />,
              key: 'log',
              label: <Text type="secondary">{`Étapes terminées (${job.steps.length})`}</Text>,
            },
          ]}
        />
      )}
    </Space>
  );
};

export default ResultPanel;
